import className from 'classnames';
import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Modal, Row, OverlayTrigger, Popover } from 'react-bootstrap';
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp } from 'react-icons/fi';
import { connect } from 'react-redux';
import { Prompt } from 'react-router-dom';
import { push } from 'react-router-redux';
import Spinner from 'react-spinkit';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { isMinimumBased, trainingPlanSortingAndGrouping } from '../../../../../services';
import { TooltipOnEllipsis } from '../../../../components';
import { currentUserIndustry } from '../../../../../state/modules/user';
import { cpdCategoryType } from '../../../../../constants';
import EntityTrainingPlan from './entity-training-plan';

const PreventRouteChangeModal = ({ show, confirm, cancel }) => {
  return <Modal show={show} onHide={cancel}>
    <Modal.Header>
      <Modal.Title componentClass='h2' className='text-center'>
        Save your training plan
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className='text-center bottom-gap'>
      You are editing a training plan, do you want to leave without saving your changes?
    </Modal.Body>
    <Modal.Footer className='text-center confirmation-footer'>
      <Row>
        <Col xs={12}>
          <Button onClick={confirm}>
            Yes
          </Button>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <Button bsStyle='plain' onClick={cancel}>
            Cancel
          </Button>
        </Col>
      </Row>
    </Modal.Footer>
  </Modal>
}

const SaveErrorModal = ({ show, message, onOk, onCancel }) => {
  return (
    <Modal show={show} onHide={onCancel}>
      <Modal.Header>
        <Modal.Title componentClass='h2' className='text-center'>
          Unable to save your training plan
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='text-center bottom-gap'>
        {message}
      </Modal.Body>
      <Modal.Footer className='text-center confirmation-footer'>
        <Row>
          <Col xs={12}>
            <Button onClick={onOk}>
              OK
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  )
}

const TrainingPlanEdit = ({
  cpdCategoryHeadings,
  trainingPlans,
  trainingPlanControls,
  hasMandatoryCategory,
  nextRoute,
  cancelRouteChange,
  confirmRouteChange,
  setNextRoute,
  isEditingTrainingPlan,
  handleScroll,
  scrollLeft,
  scrollRight,
  setScrollRef,
  setScrollLeft,
  setScrollRightOrLoadMore,
  externalControls,
  batchSaveTrainingPlans,
  clearChanges,
  multipleEntitiesFooter,
  isShowingFooter,
  showSaveErrorModal,
  saveError,
  setShowSaveErrorModal,
}) => {
  return (
    <div id='training-plan-edit'
      className={className({ multipleEntity: !isEditingTrainingPlan, multipleEntityEditing: isEditingTrainingPlan })}>
      <Prompt when={!nextRoute && isEditingTrainingPlan} message={(nextLocation) => {
        setNextRoute(nextLocation)
        return false
      }} />
      <PreventRouteChangeModal show={!fp.isEmpty(nextRoute)} confirm={confirmRouteChange} cancel={cancelRouteChange} />
      <SaveErrorModal
        show={showSaveErrorModal && saveError !== ''}
        message={saveError}
        onOk={() => setShowSaveErrorModal(false)}
        onCancel={() => setShowSaveErrorModal(false)}
      />
      {
        cpdCategoryHeadings.singleEntityCpdRequirementControl ?
          <div id='single-entity-cpd-category-settings' className='cpd-category-heading heading no-padding flexbox center'>
            <div className='flexbox align-center mobile'>
              {cpdCategoryHeadings.singleEntityCpdRequirementControl}
            </div>
          </div> : null
      }
      <div id='mobile-entity-training-plan-scroll' className='flexbox justify-between'>
        {
          scrollLeft.show ?
            <div className={className('flexbox center align-center scroll-button scroll-left')} onClick={setScrollLeft}><FiChevronLeft />prev</div> :
            <div className={className('flexbox center align-center scroll-button scroll-left disabled')}><FiChevronLeft />prev</div>
        }
        {
          scrollRight.show ?
            <div className={className('flexbox center align-center scroll-button scroll-right')} onClick={setScrollRightOrLoadMore}>next<FiChevronRight /></div> :
            <div className={className('flexbox center align-center scroll-button scroll-right disabled')}>next<FiChevronRight /></div>
        }
      </div>
      <div className='flexbox nowrap'>
        <div id='cpd-category-headings' style={{ minWidth: '300px' }}>
          {cpdCategoryHeadings.headings}
        </div>
        <div id='entity-training-plans-shadow'>
          {
            scrollLeft.show && scrollRight.show ?
              <div id='shadow' className='both-sides'></div> :
              null
          }
          {
            scrollLeft.show && !scrollRight.show ?
              <div id='shadow' className='left-side'></div> :
              null
          }
          {
            !scrollLeft.show && scrollRight.show ?
              <div id='shadow' className='right-side'></div> :
              null
          }
          <div id='entity-training-plans-scroll' ref={setScrollRef} onScroll={handleScroll}>
            <div id='entity-training-plans'>
              {trainingPlans}
            </div>
          </div>
        </div>
        <div id='training-plan-controls'>
          {trainingPlanControls}
        </div>
      </div>
      {
        hasMandatoryCategory ?
          <div className='message'>* indicates a mandatory category</div> :
          null
      }
      {
        externalControls ?
          <div id='mobile-external-controls'>
            {externalControls({ trainingPlans })}
          </div>
          : null
      }
      {
        multipleEntitiesFooter && (isEditingTrainingPlan || isShowingFooter) ? multipleEntitiesFooter(batchSaveTrainingPlans, clearChanges, isEditingTrainingPlan) : null
      }
    </div>)
}

const reloadEntityTrainingPlanField = (fieldName) => ({ trainingPlans }, { entityTrainingPlans }) => () => ({
  trainingPlans: fp.flow(
    fp.filter(
      (trainingPlan) => {
        const includedInTrainingPlans = fp.find(
          ({ entity }) => entity.id === trainingPlan.entity.id
        )(entityTrainingPlans)
        return includedInTrainingPlans
      }
    ),
    fp.map(
      (trainingPlan) => ({
        ...trainingPlan,
        [fieldName]: fp.flow(
          fp.find(
            ({ entity }) => entity.id === trainingPlan.entity.id
          ),
          fp.get(fieldName)
        )(entityTrainingPlans)
      })
    )
  )(trainingPlans)
})

const mapStateToProps = (state) => ({
  industry: currentUserIndustry(state),
  industriesWithGoals: state.industry.industriesWithCpdCategorys
})
const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ industryCpdCategorys, industry, industriesWithGoals }) => {
      const industryId = fp.get('id')(fp.first(industry))
      const defaultProgressLabel = fp.getOr('Total Required')('[0].defaultProgressLabel')(industry)
      return ({
        defaultProgressLabel,
        sortedIndustryCpdCategorys: fp.flow(
          trainingPlanSortingAndGrouping
        )(industryCpdCategorys),
        isMinimumBased: isMinimumBased(industry),
        industryId,
        industryGoals: fp.flow(
          fp.filter(
            (industryWithGoals) => industryWithGoals.industry.id === industryId
          ),
          fp.flatMap('goals')
        )(industriesWithGoals)
      })
    }
  ),
  withStateHandlers(
    ({ sortedIndustryCpdCategorys, entityTrainingPlans }) => ({
      cpdCategorys: fp.map(
        (cpdCategory) => ({
          ...cpdCategory,
          showChildren: false
        })
      )(sortedIndustryCpdCategorys),
      trainingPlans: fp.map(
        (entityTrainingPlan) => ({
          ...entityTrainingPlan,
          isEditing: false
        })
      )(entityTrainingPlans)
    }),
    {
      reloadTrainingPlans: reloadEntityTrainingPlanField('trainingPlan'),
      reloadUpdatingStatus: reloadEntityTrainingPlanField('isUpdating'),
      reloadIsSaved: reloadEntityTrainingPlanField('isSaved'),
      reloadEntity: reloadEntityTrainingPlanField('entity'),
      reloadGoals: reloadEntityTrainingPlanField('goals'),
      reloadOnSave: reloadEntityTrainingPlanField('onSave'),
      reloadHasFailed: ({ trainingPlans }) => (entityTrainingPlans) => ({
        trainingPlans: fp.map(
          (trainingPlan) => {
            const failedTrainingPlan = fp.find(
              (plan) => plan.entity.id === trainingPlan.entity.id && plan.hasFailed
            )(entityTrainingPlans)
            return failedTrainingPlan ?
              { ...failedTrainingPlan, isEditing: false } :
              { ...trainingPlan, hasFailed: entityTrainingPlans.hasFailed }
          }
        )(trainingPlans)
      }),
      reloadData: ({ trainingPlans }) => (entityTrainingPlans) => {
        const existingEntityIds = fp.map('entity.id')(trainingPlans)
        const newTrainingPlans = fp.filter(
          ({ entity }) => !fp.contains(entity.id)(existingEntityIds)
        )(entityTrainingPlans)
        const remainingTrainingPlans = fp.filter(
          ({ entity }) => fp.includes(entity.id)(fp.map('entity.id')(entityTrainingPlans))
        )(trainingPlans)
        return {
          trainingPlans: remainingTrainingPlans.concat(
            fp.map(
              (entityTrainingPlan) => ({
                ...entityTrainingPlan,
                isEditing: false
              })
            )(newTrainingPlans)
          )
        }
      },
      clearChanges: (_, { entityTrainingPlans }) => () => ({
        trainingPlans: fp.map(
          (entityTrainingPlan) => ({
            ...entityTrainingPlan,
            isEditing: false,
          })
        )(entityTrainingPlans)
      }),
      toggleEditing: ({ trainingPlans }) => (entityId) => ({
        trainingPlans: fp.map(
          (entityTrainingPlan) => entityTrainingPlan.entity.id === entityId ? {
            ...entityTrainingPlan,
            isEditing: !entityTrainingPlan.isEditing
          } : entityTrainingPlan
        )(trainingPlans)
      }),
      toggleChidlren: ({ cpdCategorys }) => (parentIdToToggle) => ({
        cpdCategorys: fp.map(
          ({ parent, showChildren, ...rest }) => ({
            ...rest,
            parent,
            showChildren: parentIdToToggle === parent.id ? true : true
          })
        )(cpdCategorys)
      }),
      setIndustryEntity: ({ trainingPlans }) => (entity, industryEntity) => ({
        trainingPlans: fp.map(
          (plan) => plan.entity.id === entity.id ? {
            ...plan,
            industryEntity
          } : plan
        )(trainingPlans)
      }),
      setTrainingPlan: ({ trainingPlans }) => (entityId, trainingPlan) => ({
        trainingPlans: fp.map(
          (entityTrainingPlan) => entityTrainingPlan.entity.id === entityId ? {
            ...entityTrainingPlan,
            trainingPlan
          } : entityTrainingPlan
        )(trainingPlans)
      }),
      setProgressGoal: ({ trainingPlans }) => (entityId, goals) => ({
        trainingPlans: fp.map(
          (entityTrainingPlan) => entityTrainingPlan.entity.id === entityId ? {
            ...entityTrainingPlan,
            goals
          } : entityTrainingPlan
        )(trainingPlans)
      })
    }
  ),
  withHandlers({
    saveEntityTrainingPlan: ({ trainingPlans, industryId, toggleEditing }) => (entityId) => {
      const entityTrainingPlan = fp.find(
        ({ entity }) => entity.id === entityId
      )(trainingPlans)
      if (entityTrainingPlan) {
        const { entity, trainingPlan, industryEntity, onSave, goals } = entityTrainingPlan
        const callback = () => {
          toggleEditing(entityId)
        }
        onSave({ entity, trainingPlan, industryId, industryEntity, goals, callback })
      }
    },
    batchSaveTrainingPlans: ({ onSaveMultiplePlans, industryId, trainingPlans, toggleEditing }) => () => {
      const plansToUpdate = fp.filter(({ isEditing }) => isEditing)(trainingPlans)
      const callback = (entityId) => {
        toggleEditing(entityId)
      }
      onSaveMultiplePlans({ plansToUpdate, industryId, callback })
    }
  }),
  withProps(
    ({ trainingPlans }) => ({
      isEditingTrainingPlan: fp.any(
        ({ isEditing }) => isEditing
      )(trainingPlans)
    })
  ),
  withProps(
    () => ({
      getDisplacement: (target, done) => {
        const scrollSpeed = 1
        const displacement = Math.floor(Math.max(1, done) * (target - done) * scrollSpeed) / (target)
        return Math.max(displacement, 1)
      },
      canScrollRight: (elem) => {
        return elem.scrollWidth > (elem.scrollLeft + elem.clientWidth)
      },
      calcFramedTarget: (clientWidth, currentPos, direction) => {
        const widthPerFrame = 130
        const residue = (currentPos % widthPerFrame)
        const justifiedResidue = (residue > widthPerFrame / 2) ? (widthPerFrame - residue) : residue
        const justifiedWidth = (Math.floor(clientWidth / widthPerFrame) * widthPerFrame) + (direction * justifiedResidue)
        return justifiedWidth
      }
    })
  ),
  withStateHandlers(
    {
      scrollRef: undefined,
      scrollLeft: {
        show: false
      },
      scrollRight: {
        show: false
      }
    },
    {
      setScrollRef: () => (scrollRef) => ({ scrollRef }),
      setScroll: ({ scrollLeft, scrollRight }, { hasMore, canScrollRight }) => (elem) => {
        const showScrollLeft = elem.scrollLeft > 0
        const showScrollRight = canScrollRight(elem) || hasMore
        const nextScrollLeft = {
          ...scrollLeft,
          show: showScrollLeft
        }
        const nextScrollRight = {
          ...scrollRight,
          show: showScrollRight
        }
        if (fp.isEqual([scrollLeft, scrollRight])([nextScrollLeft, nextScrollRight])) {
          return {}
        } else {
          return ({
            scrollLeft: nextScrollLeft,
            scrollRight: nextScrollRight
          })
        }
      },
      setScrollLeft: ({ scrollLeft, scrollRef }, { calcFramedTarget }) => () => {
        const target = Math.min(calcFramedTarget(scrollRef.clientWidth, scrollRef.scrollLeft, -1), scrollRef.scrollLeft)
        return {
          scrollLeft: {
            ...scrollLeft,
            target,
            done: 0
          }
        }
      },
      setScrollRight: ({ scrollRight, scrollRef }, { calcFramedTarget }) => () => {
        const target = Math.min(calcFramedTarget(scrollRef.clientWidth, scrollRef.scrollLeft, 1), (scrollRef.scrollWidth - scrollRef.scrollLeft))
        return {
          scrollRight: {
            ...scrollRight,
            target,
            done: 0
          }
        }
      },
      updateScrollLeft: ({ scrollLeft }) => (displacement) => {
        return {
          scrollLeft: {
            ...scrollLeft,
            done: scrollLeft.done + displacement
          }
        }
      },
      updateScrollRight: ({ scrollRight }) => (displacement) => {
        return {
          scrollRight: {
            ...scrollRight,
            done: scrollRight.done + displacement
          }
        }
      }
    }
  ),
  withHandlers({
    scrollToLeft: ({ scrollRef, scrollLeft, updateScrollLeft, getDisplacement }) => () => {
      const {
        target,
        done
      } = scrollLeft
      const displacement = getDisplacement(target, done)
      const leftLimit = 0
      scrollRef.scrollLeft = Math.max(leftLimit, scrollRef.scrollLeft - displacement)
      updateScrollLeft(displacement)
    },
    scrollToRight: ({ scrollRef, scrollRight, updateScrollRight, getDisplacement }) => () => {
      const {
        target,
        done
      } = scrollRight
      const displacement = getDisplacement(target, done)
      const rightLimit = scrollRef.scrollWidth - scrollRef.clientWidth
      scrollRef.scrollLeft = Math.min(rightLimit, scrollRef.scrollLeft + displacement)
      updateScrollRight(displacement)
    },
    setScrollRightOrLoadMore: ({ setScrollRight, scrollRef, loadMore }) => () => {
      if (scrollRef.scrollWidth > (scrollRef.scrollLeft + scrollRef.clientWidth)) {
        setScrollRight()
      } else {
        if (loadMore) {
          loadMore()
        }
      }
    }
  }),
  withHandlers({
    handleScroll: ({ setScroll, canScrollRight, loadMore }) => (e) => {
      const elem = e.target
      setScroll(elem)
      if (!canScrollRight(elem)) {
        if (loadMore) {
          loadMore()
        }
      }
    }
  }),
  lifecycle({
    componentDidMount() {
      const {
        setScroll
      } = this.props
      const scroll = document.getElementById('entity-training-plans-scroll')
      setScroll(scroll)
    },
    componentDidUpdate({ entityTrainingPlans: prevEntityTrainingPlans, isEditingTrainingPlan: wasEditingTrainingPlan }) {
      const {
        entityTrainingPlans,
        reloadTrainingPlans,
        cpdCategorys,
        toggleChidlren,
        reloadUpdatingStatus,
        reloadIsSaved,
        reloadHasFailed,
        reloadEntity,
        reloadGoals,
        reloadOnSave,
        reloadData,
        isEditingTrainingPlan,
        updateEditingStatus,
        setScroll,
        scrollLeft,
        scrollRight,
        scrollToLeft,
        scrollToRight
      } = this.props

      const scroll = document.getElementById('entity-training-plans-scroll')
      setScroll(scroll)

      setTimeout(
        () => {
          if (scrollLeft && scrollRight) {
            if (scrollLeft.target > scrollLeft.done) {
              scrollToLeft()
            }
            if (scrollRight.target > scrollRight.done) {
              scrollToRight()
            }
          }
        },
        30
      )

      if (!fp.isEqual(isEditingTrainingPlan)(wasEditingTrainingPlan)) {
        if (updateEditingStatus) {
          updateEditingStatus(isEditingTrainingPlan)
        }
      }

      if (!fp.isEqual(entityTrainingPlans)(prevEntityTrainingPlans)) {
        const hasEntityTrainingPlanFieldUpdated = (fieldName) => !fp.isEqual(
          fp.map(fieldName)(entityTrainingPlans)
        )(
          fp.map(fieldName)(prevEntityTrainingPlans)
        )

        if (!hasEntityTrainingPlanFieldUpdated('entity.id')) {
          if (hasEntityTrainingPlanFieldUpdated('isUpdating')) {
            reloadUpdatingStatus()
          }

          if (hasEntityTrainingPlanFieldUpdated('isSaved')) {
            reloadIsSaved()
          }

          if (hasEntityTrainingPlanFieldUpdated('hasFailed')) {
            reloadHasFailed(entityTrainingPlans)
          }

          if (hasEntityTrainingPlanFieldUpdated('entity')) {
            reloadEntity()
          }

          if (hasEntityTrainingPlanFieldUpdated('goals')) {
            reloadGoals()
          }

          if (hasEntityTrainingPlanFieldUpdated('onSave')) {
            reloadOnSave()
          }

          if (hasEntityTrainingPlanFieldUpdated('trainingPlan')) {
            reloadTrainingPlans()

            const isChildrenAllSelectedOrDeselected = ({ parent, children, showChildren }) => {
              const allChildrenSelectedByTrainingPlan = ({ trainingPlan }) => {
                const childSelectedByTrainingPlan = ({ id }) => {
                  const trainingPlanEntry = fp.find(
                    ({ cpdCategoryId }) => cpdCategoryId === id
                  )(trainingPlan)
                  return trainingPlanEntry ? true : false
                }
                return fp.all(childSelectedByTrainingPlan)(children)
              }

              const allChildrenDeselectedByTrainingPlan = ({ trainingPlan }) => {
                const childDeselectedByTrainingPlan = ({ id }) => {
                  const trainingPlanEntry = fp.find(
                    ({ cpdCategoryId }) => cpdCategoryId === id
                  )(trainingPlan)
                  return trainingPlanEntry ? false : true
                }
                return fp.all(childDeselectedByTrainingPlan)(children)
              }

              const allChildrenSelected = fp.all(allChildrenSelectedByTrainingPlan)(entityTrainingPlans)
              const allChildrenDeselected = fp.all(allChildrenDeselectedByTrainingPlan)(entityTrainingPlans)

              return showChildren &&
                (children.length > 0) &&
                (allChildrenSelected || allChildrenDeselected)
            }
            const cpdCategoryWithAllChildrenSelectedByAllTrainingPlan = fp.find(isChildrenAllSelectedOrDeselected)(cpdCategorys)
            if (cpdCategoryWithAllChildrenSelectedByAllTrainingPlan) {
              toggleChidlren(cpdCategoryWithAllChildrenSelectedByAllTrainingPlan.parent.id)
            }
          }
        } else {
          reloadData(entityTrainingPlans)
        }

      }
    }
  }),
  withStateHandlers(
    ({ trainingPlans }) => {
      const showBreakdown = fp.flow(
        fp.flatMap('goals'),
        fp.isEmpty
      )(trainingPlans)
      return {
        nextRoute: undefined,
        showBreakdown
      }
    },
    {
      setNextRoute: () => (nextRoute) => ({
        nextRoute
      }),
      toggleBreakdown: ({ showBreakdown }) => () => ({
        showBreakdown: !showBreakdown
      })
    }
  ),
  withStateHandlers({
    showSaveErrorModal: false,
    saveError: '',
  },
  {
    setShowSaveErrorModal: () => (state) => ({
      showSaveErrorModal: state,
    }),
    setSaveError: () => (error) => ({
      saveError: error,
    }),
  }),
  withHandlers({
    confirmRouteChange: ({ setNextRoute, nextRoute, goTo }) => () => {
      if (nextRoute) {
        goTo(nextRoute.pathname)
      }
    },
    cancelRouteChange: ({ setNextRoute }) => () => {
      setNextRoute(undefined)
    }
  }),
  withProps(
    ({
      trainingPlans,
      toggleEditing,
      cpdCategorys,
      toggleChidlren,
      isMinimumBased,
      setTrainingPlan,
      setIndustryEntity,
      saveEntityTrainingPlan,
      externalControls,
      entityTitle,
      scrollLeft,
      setScrollLeft,
      scrollRight,
      setScrollRightOrLoadMore,
      isLoading,
      emptyDataText,
      loadMore,
      hideFailedBadge,
      industryGoals,
      setProgressGoal,
      toggleBreakdown,
      showBreakdown,
      defaultProgressLabel,
      setShowSaveErrorModal,
      setSaveError,
      industry,
    }) => {
      const hasMandatoryCategory = fp.find(
        ({ parent: { mandatory } }) => mandatory
      )(cpdCategorys)

      return ({
        hasMandatoryCategory,
        cpdCategoryHeadings: fp.flow(
          (cpdCategorys) => {
            const coreCpdCategories = cpdCategorys.filter(({ parent }) => parent.categoryType === cpdCategoryType.core);
            const subCpdCategories = cpdCategorys.filter(({ parent }) => parent.categoryType === cpdCategoryType.sub);

            const getCategoryHeadings = fp.map(
              ({ parent, children, showChildren }) => {
                const { id, name, mandatory } = parent
                const trainingPlanEntry = trainingPlans.length === 1 ? fp.flow(
                  fp.first,
                  fp.get('trainingPlan'),
                  fp.find(
                    ({ cpdCategoryId }) => cpdCategoryId === id
                  )
                )(trainingPlans) : null
                const hasChildren = children.length > 0
                return <div key={id} className={className('cpd-category-group', { hasChildren, showChildren: hasChildren && showChildren })}>
                  <div className='cpd-category-heading cell flexbox center vertical'>
                    <TooltipOnEllipsis
                      component={<p id='cpd-category-name'>&nbsp;&nbsp;{he.decode(name) + (mandatory ? '*' : '')}</p>} />
                    {
                      isMinimumBased && trainingPlanEntry ?
                        <p id='minimum-points-label' className='no-margin'>&nbsp;&nbsp;{`Minimum ${trainingPlanEntry.totalCpdPoints} hours`}</p> :
                        null
                    }
                    {
                      hasChildren ?
                        <div id='child-category-toggle-box' className='flexbox'>
                          <div id='select-sub-category-label' className='flexbox vertical'>
                            <span id='box-title'>Select {fp.toLower(he.decode(name))} sub-categories</span>
                            {/* keept these code around before we are sure about removing the logic */}
                            {/* {
                              showChildren ?
                                <div id='box-text'>Select the sub-categorys an individual must complete to meet the minimum hours for this category. An individual may choose their own allocation of any unassigned hours.</div> :
                                <div id='box-text'>If left unselected an individual may complete any sub-category to gain minimum CPD hours for this category.</div>
                            } */}
                            <div id='box-text'>
                              These will appear under the Technical CPD library. You can select all, or select specific RG146 technical content to show in your library. If no sub-categories are selected, all technical content will appear in the library.
                            </div>
                          </div>
                        </div> :
                        null
                    }
                  </div>
  
                  {
                    showChildren ? fp.map(
                      (child) => {
                        return <div key={child.id} className='cpd-category-heading cell child flexbox center vertical'>
                          <TooltipOnEllipsis
                            component={<p id='cpd-category-name'>{he.decode(child.name)}</p>} />
                        </div>
                      }
                    )(children) : null
                  }
                </div>
              }
            );

            return {
              coreCategoryHeadings: getCategoryHeadings(coreCpdCategories),
              subCategoryHeadings: getCategoryHeadings(subCpdCategories),
            };
          },
          ({ coreCategoryHeadings, subCategoryHeadings }) => {
            const industryGoalHeadings = fp.map(
              ({ id, name }) => {
                return <div key={`industry-goal-${id}`} className='industry-goal-heading cell flexbox align-center'>{name}</div>
              }
            )(industryGoals)
            return {
              coreCategoryHeadings: showBreakdown ? coreCategoryHeadings : [],
              subCategoryHeadings: showBreakdown ? subCategoryHeadings : [],
              industryGoalHeadings,
            }
          },
          ({ coreCategoryHeadings, subCategoryHeadings, industryGoalHeadings }) => {
            const multipleEntityHeadings = <div id='multiple-entity-headings'>
              <div id='multiple-entity-name-heading'>{entityTitle}</div>
              <div id='multiple-entity-date-heading'>CPD year ends</div>
            </div>

            const totalCpdHeading = cpdCategorys.length > 1 ? [
              <div key='default-progress-goal-heading' className='industry-goal-heading cell flexbox align-center'>
                {defaultProgressLabel}
                <div id='breakdown'>
                  <Button bsStyle='plain' className='right flexbox align-center' onClick={toggleBreakdown}>
                    {showBreakdown ? 'Hide' : 'See'} breakdown
                  {showBreakdown ? <FiChevronUp /> : <FiChevronDown />}
                  </Button>
                </div>
              </div>
            ] : [];

            const coreCategoryTitleHeading = coreCategoryHeadings.length > 0 ? [
              <div 
                key='core-category-heading'
                className='industry-goal-heading cell flexbox align-center'
                style={{ background: 'lightgrey' }}
              >
                {fp.getOr('Core Topics')('[0].coreTopicLabel')(industry)}:&nbsp;
                <OverlayTrigger trigger='click' placement='right' overlay={(
                  <Popover id='core-topic-explain'>
                    Retail advisers are required to complete legislative requirements of 40hr CPD per year.
                    This includes 9hr in Professionalism and Ethics, 5hr in Compliance & Consumer Protection,
                    5hr in Client Care & Practice, 5hr in Technical Competence and no minimum hours for General.
                    Relevant Providers who provide Tax (Financial) Advice must also complete 5hr in this topic.
                    See&nbsp;
                    <a
                      href='https://fas.treasury.gov.au/other-standards/continuing-professional-development'
                      target='_blank'
                      rel="noopener noreferrer"
                    >here</a>
                    &nbsp;for more details.
                  </Popover>
                )}>
                  <Button bsStyle='plain' className='right flexbox align-center'>
                    What's this?
                  </Button>
                </OverlayTrigger>
              </div>
            ] : [];

            const subCategoryTitleHeading = subCategoryHeadings.length > 0 ? [
              <div
                key='sub-category-heading'
                className='industry-goal-heading cell flexbox align-center'
                style={{ background: 'lightgrey' }}
              >
                {fp.getOr('Sub Topics')('[0].subTopicLabel')(industry)}:&nbsp;
                <OverlayTrigger trigger='click' placement='right' overlay={(
                  <Popover id='sub-topic-explain'>
                    Subtopics (ASIC knowledge areas) can be selected to ensure relevant topics are covered in a users CPD training.
                    ASIC Knowledge areas typically fall under Technical Competence libraries,
                    but in some instances will be mapped to other core categories.
                    You can select topics to include in a Technical Competence library,
                    or assign specific hours for users to complete against their training plans.
                  </Popover>
                )}>
                  <Button bsStyle='plain' className='right flexbox align-center'>
                    What's this?
                  </Button>
                </OverlayTrigger>
              </div>
            ] : [];

            return {
              headings: [
                <div key={0} id='multiple-entity-cpd-category-settings' className='cpd-category-heading heading cell no-padding'>
                  {
                    scrollLeft.show ?
                      <div id='entity-training-plans-scroll-left' className='flexbox center' onClick={setScrollLeft}><FiChevronLeft /></div> :
                      null
                  }
                  <div className='flexbox end'>
                    {multipleEntityHeadings}
                  </div>
                </div>
              ].concat(totalCpdHeading)
              .concat(coreCategoryTitleHeading).concat(coreCategoryHeadings)
              .concat(subCategoryTitleHeading).concat(subCategoryHeadings)
              .concat(industryGoalHeadings)
            }
          }
        )(cpdCategorys),

        trainingPlans: fp.map(
          (trainingPlan) => <EntityTrainingPlan
            key={trainingPlan.entity.id}
            showTrainingPlanRequirements={true}
            multiple={trainingPlans.length > 1}
            {...trainingPlan}
            cpdCategorys={cpdCategorys}
            setTrainingPlan={setTrainingPlan}
            setIndustryEntity={setIndustryEntity}
            setProgressGoal={setProgressGoal}
            saveEntityTrainingPlan={saveEntityTrainingPlan}
            toggleEditing={toggleEditing}
            hideFailedBadge={hideFailedBadge}
            showBreakdown={showBreakdown}
            setShowSaveErrorModal={setShowSaveErrorModal}
            setSaveError={setSaveError}
          />
        )(trainingPlans),

        trainingPlanControls: fp.flow(
          (cpdCategorys) => {
            const coreCpdCategories = cpdCategorys.filter(({ parent }) => parent.categoryType === cpdCategoryType.core);
            const subCpdCategories = cpdCategorys.filter(({ parent }) => parent.categoryType === cpdCategoryType.sub);

            const getControls = fp.map(
              ({ parent, children, showChildren }) => {
                const { id } = parent
                const hasChildren = children.length > 0
                return <div key={id} className={className('cpd-category-group', { hasChildren, showChildren: hasChildren && showChildren })}>
                  <div className='training-plan-control cell flexbox center align-center'></div>
                  {
                    showChildren ? fp.map(
                      (child) => <div key={child.id} className='training-plan-control cell child flexbox center align-center'></div>
                    )(children) : null
                  }
                </div>
              }
            );

            return {
              coreCategoryControls: getControls(coreCpdCategories),
              subCategoryControls: getControls(subCpdCategories),
            };
          },
          ({ coreCategoryControls, subCategoryControls }) => {
            const industryGoalControls = <div key='industry-goal-controls' className='cpd-category-group'>
              {
                fp.map(
                  ({ id }) => <div key={`industry-goal-${id}`} className='training-plan-control industry-goal cell child flexbox center align-center'></div>
                )(industryGoals)
              }
            </div>
            return {
              coreCategoryControls: showBreakdown ? coreCategoryControls : [],
              subCategoryControls: showBreakdown ? subCategoryControls : [],
              industryGoalControls: [industryGoalControls],
            }
          },
          ({ coreCategoryControls, subCategoryControls, industryGoalControls }) => {
            const totalCpdControl = cpdCategorys.length > 0 ? [
              <div key='default-progress-goal-control' className='cpd-category-group'>
                <div className='training-plan-control industry-goal cell flexbox align-center'></div>
              </div>
            ] : [];

            const coreCategoryTitleControl = coreCategoryControls.length > 0 ? [
              <div key='core-category-control' className='cpd-category-group'>
                <div
                  className='training-plan-control industry-goal cell flexbox align-center'
                  style={{ background: 'lightgrey' }}
                ></div>
              </div>
            ] : [];

            const subCategoryTitleControl = subCategoryControls.length > 0 ? [
              <div key='sub-category-control' className='cpd-category-group' style={{ background: 'lightgrey' }}>
                <div
                  className='training-plan-control industry-goal cell flexbox align-center'
                  style={{ background: 'lightgrey' }}
                ></div>
              </div>
            ] : [];
            
            return [
              <div key={0} className='training-plan-control heading cell flexbox align-center'>
                {
                  externalControls ? externalControls({ trainingPlans }) : null
                }
                {
                  trainingPlans.length === 0 ? <p className='no-training-plan-text flex-center'>{emptyDataText}</p> : null
                }
                {
                  scrollRight.show ?
                    isLoading ?
                      <div id='entity-training-plans-scroll-right' className='flexbox center'><span><Spinner name='circle' className='spinner' fadeIn='none' /></span></div> :
                      <div id='entity-training-plans-scroll-right' className='flexbox center' onClick={setScrollRightOrLoadMore}><FiChevronRight /></div> :
                    null
                }
              </div>
            ].concat(totalCpdControl)
            .concat(coreCategoryTitleControl).concat(coreCategoryControls)
            .concat(subCategoryTitleControl).concat(subCategoryControls)
            .concat(industryGoalControls)
          },
        )(cpdCategorys)
      })
    }
  )
)(TrainingPlanEdit);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/components/training-plan-edit.js